import React, {useEffect, useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import Analytics from 'utils/Analytics';
import {PageModel} from 'survey-core';
import {completeSurvey} from 'screens/InsomniaSurvey/utils/completeSurvey';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {useSurveyError} from 'screens/InsomniaSurvey/contexts/SurveyErrorContext';

const getNextDelayMs = (page: PageModel) => {
  //@ts-expect-error We need to access the custom `riseProperties` property, and this is the only way I know how.
  const {delayOnNext} = page?.jsonObj?.riseProperties ?? {};
  return delayOnNext;
};

const getOnNextCallback = (page: PageModel) => {
  //@ts-expect-error We need to access the custom `riseProperties` property, and this is the only way I know how.
  const {onNext} = page?.jsonObj?.riseProperties ?? {};
  return onNext;
};

const allRequiredQuestionsAnswered = (page: PageModel) => {
  return page.questions.every((question: any) => {
    // If the question is not required, skip validation for it
    if (!question.isRequired) {
      return true;
    }
    // For required questions, check if they have a value with length > 0
    return question.value !== undefined && question.value?.length > 0;
  });
};

const trackTextAnswersSubmitted = (page: PageModel) => {
  page.questions
    .filter((question: any) => question.getType?.() === 'text')
    .forEach((question: any) => {
      Analytics.track('Answer selected', {
        survey_name: surveyInstance.title,
        question_title: question.title,
        question_name: question.name,
        question_value: question.value,
        pageNumber: surveyInstance.currentPageNo,
      });
    });
};

export const CompleteButton: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const {setError} = useSurveyError();
  const page = surveyInstance.currentPage;
  const nextDelayMs = getNextDelayMs(page);
  const isInterstitial = page.isInterstitial;
  const [disabled, setDisabled] = useState(!isInterstitial ? !allRequiredQuestionsAnswered(page) : false);
  useEffect(() => {
    const handleValueChanged = (sender: any) => {
      setDisabled(!allRequiredQuestionsAnswered(sender.currentPage));
      // Clear error when user changes values
      setError(null);
    };

    surveyInstance.onValueChanged.add(handleValueChanged);
    return () => {
      surveyInstance.onValueChanged.remove(handleValueChanged);
    };
  }, [setError]);

  return (
    <ButtonSolid
      title={'Complete'}
      loading={loading}
      disabled={disabled}
      onClick={async () => {
        const page = surveyInstance.currentPage;
        setDisabled(true);
        setError(null);
        Analytics.track('Complete clicked', {
          survey_name: surveyInstance.title,
          question_title: page.questions[0].title,
          question_name: page.questions[0].name,
          question_value: page.questions[0].value,
        });

        // Call the onNext callback if it exists
        const onNext = getOnNextCallback(page);
        if (onNext) {
          try {
            await onNext(surveyInstance.data);
          } catch (error) {
            console.error('Error in onNext callback:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred. Please try again.';
            setError(errorMessage);
            setDisabled(false);
            return; // Stop execution and don't complete the survey
          }
        }

        trackTextAnswersSubmitted(page);

        if (nextDelayMs > 0) {
          setLoading(true);
          setTimeout(() => {
            setLoading(false);
            completeSurvey(surveyInstance);
          }, nextDelayMs);
        } else {
          completeSurvey(surveyInstance);
        }
      }}
    />
  );
};
